import React, { useState, useRef, useEffect } from 'react';
import './ImageComparisonSlider.css';

const ImageComparisonSlider = ({ beforeImage, afterImage, alt }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [imagesLoaded, setImagesLoaded] = useState({ before: false, after: false });
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  
  // Use direct references to the hosted images
  const beforeImageSrc = '/images/before-render.png';
  const afterImageSrc = '/images/after-render.png';
  
  // Pre-load images to ensure they're in the browser cache
  useEffect(() => {
    const preloadImage = (src) => {
      const img = new Image();
      img.src = src;
      return img;
    };
    
    const beforeImg = preloadImage(beforeImageSrc);
    const afterImg = preloadImage(afterImageSrc);
    
    beforeImg.onload = () => {
      console.log('Before image pre-loaded');
      setImagesLoaded(prev => ({ ...prev, before: true }));
    };
    
    afterImg.onload = () => {
      console.log('After image pre-loaded');
      setImagesLoaded(prev => ({ ...prev, after: true }));
    };
    
    beforeImg.onerror = () => console.error('Failed to preload before image:', beforeImageSrc);
    afterImg.onerror = () => console.error('Failed to preload after image:', afterImageSrc);
    
    return () => {
      beforeImg.onload = null;
      afterImg.onload = null;
      beforeImg.onerror = null;
      afterImg.onerror = null;
    };
  }, [beforeImageSrc, afterImageSrc]);

  // Mouse/touch event handlers
  const handleInteractionStart = (e) => {
    e.preventDefault();
    if (!containerRef.current) return;
    isDragging.current = true;
    updateSliderPosition(e.type.includes('mouse') ? e.clientX : e.touches[0].clientX);
  };

  const handleInteractionEnd = () => {
    isDragging.current = false;
  };

  const handleInteractionMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    updateSliderPosition(clientX);
  };

  const updateSliderPosition = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const position = (x / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, position)));
  };

  // Add event listeners
  useEffect(() => {
    const handleMouseUp = () => isDragging.current = false;
    const handleMouseMove = (e) => {
      if (isDragging.current) updateSliderPosition(e.clientX);
    };
    const handleTouchEnd = () => isDragging.current = false;
    const handleTouchMove = (e) => {
      if (isDragging.current && e.touches[0]) updateSliderPosition(e.touches[0].clientX);
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Handle keyboard accessibility
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(0, prev - 5));
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(100, prev + 5));
      e.preventDefault();
    }
  };

  return (
    <div 
      className="image-comparison-container"
      ref={containerRef}
      onMouseDown={handleInteractionStart}
      onTouchStart={handleInteractionStart}
      tabIndex="0"
      aria-label="Before and after image comparison slider. Use arrow keys to move the slider left and right."
      onKeyDown={handleKeyDown}
    >
      <div className="before-label" style={{ left: 16, right: 'auto' }}>BEFORE</div>
      <div className="before-label" style={{ left: 'auto', right: 16 }}>AFTER</div>
      {/* Show loading message if images aren't loaded */}
      {(!imagesLoaded.before || !imagesLoaded.after) && (
        <div className="image-loading-indicator">
          <p>Loading comparison images...</p>
        </div>
      )}
      
      {/* Images container */}
      <div className="images-wrapper">
        {/* Before Image - Shows on left side of slider */}
        <div 
          className="image-container before-image"
          style={{ 
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
          }}
        >
          <img 
            src={beforeImageSrc} 
            alt={`Before: ${alt}`} 
          />
        </div>

        {/* After Image - Shows on right side of slider */}
        <div 
          className="image-container after-image"
          style={{ 
            clipPath: `inset(0 0 0 ${sliderPosition}%)` 
          }}
        >
          <img 
            src={afterImageSrc} 
            alt={`After: ${alt}`} 
          />
        </div>

        {/* Slider divider */}
        <div 
          className="slider-divider"
          style={{ left: `${sliderPosition}%` }}
          aria-hidden="true"
        >
          <div className="slider-line"></div>
          <div className="slider-button">
            <span className="slider-arrows">⟷</span>
          </div>
        </div>

        {/* Labels */}
        <div className="before-label">BEFORE</div>
        <div className="after-label">AFTER</div>
      </div>
    </div>
  );
};

export default ImageComparisonSlider;
